<template>
  <div
    :id="`graph-canvas-${graphEditor.id}`"
    ref="containerElement"
    class="graph-canvas-container"
    @mouseenter="activateZoom(containerElement!)"
    @mouseleave="deactivateZoom"
    @dblclick.stop="onDoubleClick($event)"
  >
    <div
      ref="element"
      class="graph-canvas"
      :style="{
        '--view-size': `${CANVAS_SIZE_PX.toFixed(0)}px`,
      }"
      @mousedown.left.stop="startCanvasDrag($event)"
    >
      <div class="graph-canvas__background">
        <slot name="background" />
      </div>
      <slot name="default" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="SCHEMA extends INodeSchemaRegistry = INodeSchemaRegistry">
import {
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
} from 'vue';

import {
  GraphEditor,
} from '../core';
import {
  CANVAS_SIZE_PX,
} from '../core/constants.ts';
import {
  useDrag,
  useZoom,
} from '../hooks';
import {
  IGraphEditorEvents,
  INodeSchemaRegistry, IPosition,
} from '../types';

import {
  IUserInterfaceAdapterProps,
} from './types.ts';

const props = defineProps<IUserInterfaceAdapterProps & {
  graphEditor: GraphEditor<SCHEMA>,
}>();

defineSlots<{
  default?(): void,
  background?(): void,
}>();

const element = useTemplateRef('element');
const containerElement = useTemplateRef('containerElement');

function onCanvasChanged(event: IGraphEditorEvents['canvas:changed']) {
  if (!element.value) {
    return;
  }
  // We use transform: translate instead of position: absolute + left + right,
  // as transform css is GPU accelerated and provides some significant performance.
  // It also skips the layouting phase of the browser and thus gains another
  // performance boost
  //
  // The order is crucial. It is important that the scale happens before the translate.
  // CSS Transforms are executed from right to left:
  // https://developer.mozilla.org/en-US/docs/Web/CSS/transform#values
  element.value.style.transform = `translate(${event.pan.x}px, ${event.pan.y}px) scale(${event.zoom})`;
}

function onCanvasResize() {
  if (!element.value) {
    return;
  }
  props.graphEditor.setCanvasSize({
    x: element.value.offsetWidth,
    y: element.value.offsetHeight,
  });
}

function handleCanvasPan(deltaX: number, deltaY: number) {
  props.graphEditor.panBy(deltaX, deltaY);
}

const {
  start: startCanvasDrag,
} = useDrag({
  onPan: handleCanvasPan,
});

function handleZoom(zoomDelta: number, anchorPosition: IPosition) {
  props.graphEditor.zoomBy(zoomDelta, anchorPosition);
}

const {
  activateZoom,
  deactivateZoom,
} = useZoom({
  zoomCallback: handleZoom,
});

function onDoubleClick(event: MouseEvent) {
  const anchorScreen = props.graphEditor.clientToScreen({
    x: event.clientX,
    y: event.clientY,
  }) || {
    x: 0,
    y: 0,
  };
  props.graphEditor.zoomBy(0.5, anchorScreen);
}

let canvasResizeObserver: ResizeObserver;

onMounted(() => {
  onCanvasChanged({
    zoom: props.graphEditor.zoom,
    pan: props.graphEditor.pan,
  });

  props.graphEditor.setCanvasElement(containerElement.value!);
  canvasResizeObserver = new ResizeObserver(onCanvasResize);
  canvasResizeObserver.observe(containerElement.value!);

  props.graphEditor.addEventListener('canvas:changed', onCanvasChanged);
});

onBeforeUnmount(() => {
  props.graphEditor.addEventListener('canvas:changed', onCanvasChanged);
  canvasResizeObserver.disconnect();
});
</script>

<style scoped lang="scss">
@use './styles.scss';

.graph-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.graph-canvas {
  @include styles.draggable;

  transform-origin: 0 0;
  position: absolute;
  inset: 0;

  &__background {
    position: absolute;
    top: calc(-0.5 * var(--view-size));
    left: calc(-0.5 * var(--view-size));
    width: var(--view-size);
    height: var(--view-size);
  }

  &__background > :first-child {
    position: absolute;
    inset: 0;
  }
}
</style>
